(() => {
  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const activeDrawerSelector = 'mobile-menu[aria-hidden="false"], drawer-dialog[aria-hidden="false"], cart-drawer[aria-hidden="false"]';
  let announcementResizeObserver;
  const syncAnnouncementHeight = () => {
    const announcement = document.querySelector('.announcement-bar');
    const height = announcement ? Math.ceil(announcement.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty('--announcement-bar-height', `${height}px`);
  };
  const initAnnouncementHeight = () => {
    announcementResizeObserver?.disconnect();
    syncAnnouncementHeight();
    const announcement = document.querySelector('.announcement-bar');
    if (announcement && 'ResizeObserver' in window) {
      announcementResizeObserver = new ResizeObserver(syncAnnouncementHeight);
      announcementResizeObserver.observe(announcement);
    }
  };
  const setOverlay = (open) => {
    const overlay = document.querySelector('[data-drawer-overlay]');
    if (!overlay) return;
    if (open) {
      overlay.removeAttribute('hidden');
      requestAnimationFrame(() => overlay.classList.add('is-visible'));
      return;
    }
    overlay.classList.remove('is-visible');
    window.setTimeout(() => {
      if (!document.querySelector(activeDrawerSelector)) overlay.setAttribute('hidden', '');
    }, reducedMotion.matches ? 0 : 330);
  };
  const openDrawerElement = (drawer, trigger) => {
    drawer.lastFocus = trigger || document.activeElement;
    drawer.setAttribute('aria-hidden', 'false');
    setOverlay(true);
    document.body.classList.add('is-locked');
    requestAnimationFrame(() => {
      drawer.querySelector(focusableSelector)?.focus({ preventScroll: true });
      window.setTimeout(() => {
        if (drawer.getAttribute('aria-hidden') === 'false' && !drawer.contains(document.activeElement)) {
          drawer.querySelector(focusableSelector)?.focus({ preventScroll: true });
        }
      }, 20);
    });
  };
  const closeDrawerElement = (drawer) => {
    drawer.setAttribute('aria-hidden', 'true');
    const anyOpen = Boolean(document.querySelector(activeDrawerSelector));
    if (!anyOpen) {
      setOverlay(false);
      document.body.classList.remove('is-locked');
    }
    drawer.lastFocus?.focus?.();
  };
  const formatMoney = (cents, format = '${{amount}}') => {
    const value = (Number(cents || 0) / 100).toFixed(2);
    return format.replace(/\{\{\s*amount(?:_with_comma_separator)?\s*\}\}/, value);
  };

  class DrawerDialog extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.closeButtons = this.querySelectorAll('[data-drawer-close]');
      this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close()));
      this.addEventListener('keydown', (event) => this.onKeydown(event));
      const overlay = document.querySelector('[data-drawer-overlay]');
      if (overlay && !overlay.dataset.dialogReady) {
        overlay.dataset.dialogReady = 'true';
        overlay.addEventListener('click', () => document.querySelector(activeDrawerSelector)?.close?.());
      }
    }
    open(trigger) {
      openDrawerElement(this, trigger);
    }
    close() {
      closeDrawerElement(this);
    }
    onKeydown(event) {
      if (event.key === 'Escape') return this.close();
      if (event.key !== 'Tab') return;
      const focusable = [...this.querySelectorAll(focusableSelector)].filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
    disconnectedCallback() {}
  }

  class MobileMenu extends DrawerDialog {}
  class ModalDialog extends DrawerDialog {}

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.input = this.querySelector('input[type="number"]');
      this.querySelectorAll('button').forEach((button) => button.addEventListener('click', () => {
        const previous = this.input.value;
        button.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
        if (previous !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }));
    }
  }

  class ThemeAccordion extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.querySelector('details')?.addEventListener('toggle', (event) => {
        event.currentTarget.querySelector('summary')?.setAttribute('aria-expanded', String(event.currentTarget.open));
      });
    }
  }

  class LocalizationForm extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.querySelectorAll('select').forEach((select) => select.addEventListener('change', () => {
        const form = select.form || this.querySelector('form');
        if (select.name !== 'locale_code') {
          form?.requestSubmit?.();
          return;
        }

        const nextLocale = select.value.toLowerCase();
        const activeLocale = document.documentElement.lang.toLowerCase().split('-')[0];
        const primaryLocale = 'en';
        let pathname = window.location.pathname;
        const activePrefix = `/${activeLocale}`;

        if (activeLocale !== primaryLocale && (pathname === activePrefix || pathname.startsWith(`${activePrefix}/`))) {
          pathname = pathname.slice(activePrefix.length) || '/';
        }
        if (nextLocale !== primaryLocale) {
          pathname = `/${nextLocale}${pathname === '/' ? '' : pathname}`;
        }

        window.location.assign(`${pathname}${window.location.search}${window.location.hash}`);
      }));
    }
  }

  class ShareButton extends HTMLElement {
    connectedCallback() {
      this.querySelector('[data-share-copy]')?.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(this.dataset.url || location.href);
          const region = document.querySelector('[data-live-region]');
          if (region) region.textContent = this.dataset.success;
        } catch (_) {}
      });
    }
  }

  class StickyHeader extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.ticking = false;
      this.update = () => {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          this.classList.toggle('is-scrolled', window.scrollY > 30);
          this.ticking = false;
        });
      };
      this.update();
      window.addEventListener('scroll', this.update, { passive: true });
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this.update);
      delete this.dataset.ready;
    }
  }

  class FacetsForm extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.querySelectorAll('[data-facets-form]').forEach((form) => form.addEventListener('change', (event) => {
        if (event.target.type === 'number' || form.id.includes('Mobile')) return;
        form.submit();
      }));
      this.querySelectorAll('[data-sort-select]').forEach((select) => select.addEventListener('change', () => select.form?.submit()));
    }
  }

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready || !this.dataset.url) return;
      this.dataset.ready = 'true';
      const url = `${this.dataset.url}&section_id=${this.dataset.sectionId}`;
      fetch(url).then((response) => response.text()).then((markup) => {
        const rendered = new DOMParser().parseFromString(markup, 'text/html').querySelector('product-recommendations');
        if (rendered?.innerHTML.trim()) {
          this.innerHTML = rendered.innerHTML;
          initReveals(this);
        }
      }).catch(() => {});
    }
  }

  const revealObserver = reducedMotion.matches ? null : new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-revealed');
      observer.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

  const initReveals = (root = document) => {
    root.querySelectorAll('[data-reveal]:not(.reveal-ready)').forEach((element) => {
      const delay = Math.min(320, Math.max(0, Number(element.dataset.revealDelay || 0)));
      element.style.setProperty('--reveal-delay', `${delay}ms`);
      element.classList.add('reveal-ready');
      if (!revealObserver) element.classList.add('is-revealed');
      else revealObserver.observe(element);
    });
  };

  const parallaxInstances = new WeakMap();
  const initHeroParallax = (root = document) => {
    root.querySelectorAll('[data-hero-parallax]').forEach((hero) => {
      if (parallaxInstances.has(hero) || reducedMotion.matches) return;
      let ticking = false;
      const update = () => {
        ticking = false;
        if (!hero.isConnected || window.innerWidth < 750) {
          hero.style.setProperty('--hero-parallax-y', '0px');
          return;
        }
        const rect = hero.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) return;
        const offset = Math.max(-24, Math.min(24, -rect.top * 0.035));
        hero.style.setProperty('--hero-parallax-y', `${offset.toFixed(2)}px`);
      };
      const requestUpdate = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      };
      window.addEventListener('scroll', requestUpdate, { passive: true });
      window.addEventListener('resize', requestUpdate, { passive: true });
      requestUpdate();
      parallaxInstances.set(hero, () => {
        window.removeEventListener('scroll', requestUpdate);
        window.removeEventListener('resize', requestUpdate);
      });
    });
  };

  customElements.define('drawer-dialog', DrawerDialog);
  customElements.define('mobile-menu', MobileMenu);
  customElements.define('modal-dialog', ModalDialog);
  customElements.define('quantity-input', QuantityInput);
  customElements.define('theme-accordion', ThemeAccordion);
  customElements.define('localization-form', LocalizationForm);
  customElements.define('share-button', ShareButton);
  customElements.define('sticky-header', StickyHeader);
  customElements.define('facets-form', FacetsForm);
  customElements.define('product-recommendations', ProductRecommendations);

  initReveals();
  initHeroParallax();
  initAnnouncementHeight();

  document.addEventListener('click', (event) => {
    const drawer = [...document.querySelectorAll('[data-trigger]')].find((candidate) => {
      const selector = candidate.dataset.trigger;
      return selector && event.target.closest(selector);
    });
    if (drawer) {
      event.preventDefault();
      openDrawerElement(drawer, event.target.closest(drawer.dataset.trigger));
      return;
    }
    const closeButton = event.target.closest('[data-drawer-close]');
    const standardDrawer = closeButton?.closest('mobile-menu, drawer-dialog');
    if (standardDrawer) closeDrawerElement(standardDrawer);
  });

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('details[open]').forEach((details) => details.removeAttribute('open'));
    initReveals(event.target);
    initHeroParallax(event.target);
    requestAnimationFrame(initAnnouncementHeight);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-hero-parallax]').forEach((hero) => parallaxInstances.get(hero)?.());
    requestAnimationFrame(initAnnouncementHeight);
  });

})();
